import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Tag } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { OfferBadge } from "@/components/OfferBadge";
import { Button } from "@/components/ui/button";
import { rupees, shortDate } from "@/lib/format";
import { ListError } from "@/components/operator/ui/ListError";

/**
 * B4 (UAT 2026-10-29): one offer's page. There is no thin public `GET /offers/{id}` — the city
 * list (`GET /offers`) already carries every field this page shows (title, discount, validity,
 * minimum order, shop), so the detail is read from that same list rather than adding a route.
 */
export default function OfferDetail() {
  const { id } = useParams();
  const { t, lang, tenant } = useApp();
  const [offer, setOffer] = useState(undefined); // undefined = loading, null = not found
  const [failed, setFailed] = useState(false);
  // Bumped by Retry: re-runs the load without a page refresh (R11 NEW-R10-4).
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!tenant) return;
    setOffer(undefined);
    setFailed(false);
    api
      .get("/offers", { params: { limit: 100 } })
      .then((r) => setOffer(rows(r.data).find((o) => String(o.id) === String(id)) || null))
      .catch(() => setFailed(true));
  }, [tenant, id, attempt]);

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8">
      <Link
        to="/offers"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        data-testid="offer-back"
      >
        <ArrowLeft size={14} />
        {t("offers.backToOffers")}
      </Link>

      {failed ? (
        <ListError
          className="mt-8"
          message={t("common.connectionTrouble")}
          onRetry={() => setAttempt((n) => n + 1)}
          testId="offer-error"
        />
      ) : offer === undefined ? (
        <p className="mt-8 text-muted-foreground">{t("common.loading")}</p>
      ) : offer === null ? (
        <p className="mt-8 text-muted-foreground" data-testid="offer-not-found">
          {t("offers.detailNotFound")}
        </p>
      ) : (
        <article className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="offer-detail">
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-heading text-3xl font-bold tracking-tight">{localized(lang, offer, "title")}</h1>
            <OfferBadge offer={offer} />
          </div>
          {localized(lang, offer, "description") && (
            <p className="text-base text-muted-foreground">{localized(lang, offer, "description")}</p>
          )}
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">{t("offers.validFrom")}</dt>
              <dd className="font-medium">{shortDate(offer.starts_on, lang)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t("offers.validTill")}</dt>
              <dd className="font-medium">{shortDate(offer.ends_on, lang)}</dd>
            </div>
            {offer.min_order_minor_units != null && (
              <div>
                <dt className="text-muted-foreground">{t("offers.minOrderLabel")}</dt>
                <dd className="font-medium tabular-nums">{rupees({ minor_units: offer.min_order_minor_units })}</dd>
              </div>
            )}
          </dl>
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Tag size={14} />
            {offer.business_name}
          </p>
          {offer.business_slug && (
            <Button asChild className="rounded-full" data-testid="offer-visit-shop">
              <Link to={`/${offer.business_slug}`}>{t("offers.visitShop", { shop: offer.business_name })}</Link>
            </Button>
          )}
        </article>
      )}
    </div>
  );
}
